import { createFileRoute } from "@tanstack/react-router";
import { breadcrumbSchema, organizationSchema, webPageSchema } from "@/lib/schema";
import { PaginasRelacionadas } from "@/components/site/PaginasRelacionadas";
import { GOOGLE_RATING, GOOGLE_REVIEW_COUNT } from "@/lib/testimonials";
import { waLink } from "@/lib/whatsapp";
import { ogImageMeta } from "@/lib/og-image";
import { PageHero } from "@/components/site/PageHero";
import { PanelSection } from "@/components/site/PanelSection";
import { PageCta } from "@/components/site/PageCta";

const TITLE = "Nuestra historia | Residencia Oasis Cuernavaca";
const DESCRIPTION =
  "Residencia Oasis Cuernavaca opera desde 2004: acompañamiento 24/7, visitas sin horario y 4.9★ con 68 opiniones en Google.";
const PATH = "/nuestra-historia";

export const Route = createFileRoute("/nuestra-historia")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://oasiscuernavaca.com/nuestra-historia" },
      ...ogImageMeta(),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: "https://oasiscuernavaca.com/nuestra-historia" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            organizationSchema(),
            webPageSchema({
              name: TITLE,
              description: DESCRIPTION,
              path: PATH,
              type: "AboutPage",
            }),
            breadcrumbSchema([{ name: "Nuestra historia", path: PATH }]),
          ],
        }),
      },
    ],
  }),
  component: Page,
});

const PRINCIPIOS = [
  [
    "La familia entra cuando quiere",
    "Visitas sin horarios restrictivos: quien cuida bien no necesita esconder nada.",
  ],
  [
    "Una casa, no un pasillo",
    "Habitaciones individuales, jardín y áreas comunes en el clima de Cuernavaca.",
  ],
  [
    "Cuidado con nombre propio",
    "Programa de cuidado personalizado por residente, no una rutina igual para todos.",
  ],
  [
    "Claridad en el costo",
    "Decimos qué cubre la cuota y qué se paga aparte antes del ingreso.",
  ],
] as const;

const OBJETIVOS = [
  [
    "Mejora continua",
    "Velar constantemente por el cuidado y bienestar de nuestros residentes.",
  ],
  [
    "Proyecto integral",
    "Consolidarnos como un proyecto innovador en el cuidado de los adultos mayores.",
  ],
  [
    "Atención personalizada",
    "Ofrecer servicios de la más alta calidad, adaptados a cada persona.",
  ],
  [
    "Comunicación transparente",
    "Mantener contacto continuo y efectivo con residentes y familias, siempre poniéndolos primero.",
  ],
] as const;

function Page() {
  return (
    <>
      <PageHero
        breadcrumb="Nuestra historia"
        title="Nuestra historia: cuidando adultos mayores en Cuernavaca desde 2004"
        answer={
          <>
            Somos un equipo con experiencia en el cuidado de adultos mayores y acompañamos a familias en Cuernavaca desde 2004.
          </>
        }
      />

      <PanelSection
        tone="soft"
        eyebrow="¿Qué significa OASIS?"
        title="Organización de Atención en Salud Integral de la Senectud"
        answer="Cada letra resume nuestra razón de ser: una organización dedicada al cuidado completo de las personas mayores."
      >
        <p className="mt-5 max-w-[62ch] text-muted-foreground">
          El nombre no es casual. Nace de la idea de ser un oasis para quienes buscan un lugar
          seguro, cálido y profesional donde sus seres queridos sean atendidos con dignidad.
        </p>
      </PanelSection>

      <PanelSection
        tone="plain"
        eyebrow="Nuestra esencia"
        title="¿Qué es Residencia Oasis Cuernavaca?"
        answer="Más que una institución de retiro para adultos mayores: un hogar donde el cariño, la compasión y el acompañamiento diario crean bienestar y respeto."
      >
        <p className="mt-3 max-w-[62ch] text-muted-foreground">
          Nuestro viaje comenzó en 2004, cuando dos amigos decidieron hacer realidad su sueño de
          aportar un cambio positivo a la vida de nuestros adultos mayores.
        </p>
        <p className="mt-3 max-w-[62ch] text-muted-foreground">
          Desde entonces, trabajamos para brindar un cuidado personalizado en un ambiente que se
          siente como en casa, basado en relaciones de respeto, cariño, amor y tranquilidad.
        </p>
        <p className="mt-3 max-w-[62ch] text-muted-foreground">
          Mantenemos un modelo que prioriza la atención personal y detallada. Cada residente merece
          una atención especial, y nos esforzamos por crear un espacio seguro y acogedor que
          preserve la intimidad que disfrutaría en su propio hogar.
        </p>
        <p className="mt-3 max-w-[62ch] text-muted-foreground">
          Estamos en Cuernavaca, Morelos, la “Ciudad de la eterna primavera”. Su clima ideal,
          ubicación elevada y proximidad a la Ciudad de México hacen de este lugar el entorno
          perfecto para nuestro hogar.
        </p>
      </PanelSection>

      <PanelSection
        tone="plain"
        eyebrow="Nuestro lema"
        title="¿De dónde sale “Hogar de privilegio, amor y cariño”?"
        answer="La frase la dijo un residente al describir su vida aquí, y la adoptamos como lema."
      >
        <blockquote className="pull-quote mt-6 max-w-[52ch]">
          <p className="quote-text">Hogar de privilegio, amor y cariño.</p>
          <footer className="mt-3 text-[0.95rem] text-muted-foreground">
            — Frase de Don Moises Maislin.
          </footer>
        </blockquote>
      </PanelSection>

      <PanelSection
        tone="soft"
        eyebrow="Misión"
        title="¿Cuál es nuestra misión?"
        answer="Proporcionar un retiro digno y humano que promueva el bienestar integral de nuestros residentes y sus familias."
      >
        <p className="mt-3 max-w-[62ch] text-muted-foreground">
          A través de nuestros cuidados, aseguramos que cada día sea vivido con alegría, respeto y
          dignidad.
        </p>
      </PanelSection>

      <PanelSection
        tone="plain"
        eyebrow="Objetivos"
        title="¿Hacia dónde nos dirigimos?"
        answer="Cuatro compromisos que guían nuestro trabajo diario."
      >
        <ul className="mt-5 grid gap-5 sm:grid-cols-2">
          {OBJETIVOS.map(([t, d]) => (
            <li key={t}>
              <h3 className="font-semibold">{t}</h3>
              <p className="mt-1 text-[0.95rem] text-muted-foreground">{d}</p>
            </li>
          ))}
        </ul>
      </PanelSection>

      <PanelSection
        tone="soft"
        eyebrow="Principios"
        title="¿Qué nos importa?"
        answer="Cuatro cosas que no negociamos, y que la familia puede verificar en una visita."
      >
        <ul className="mt-5 grid gap-5 sm:grid-cols-2">
          {PRINCIPIOS.map(([t, d]) => (
            <li key={t}>
              <h3 className="font-semibold">{t}</h3>
              <p className="mt-1 text-[0.95rem] text-muted-foreground">{d}</p>
            </li>
          ))}
        </ul>
      </PanelSection>

      <PageCta
        waHref={waLink("nuestra historia", "quiero agendar una visita")}
        eyebrow="Conózcanos"
        title="¿Cómo la conoce antes de decidir?"
        answer="Con una visita guiada, sin compromiso: ve la casa, el jardín y a los residentes."
      />

      <PaginasRelacionadas
        items={[
          {
            to: "/testimonios",
            label: "Testimonios de familias",
            hint: `Reseñas reales de Google: ${GOOGLE_RATING}★ con ${GOOGLE_REVIEW_COUNT} opiniones.`,
          },
          {
            to: "/galeria-de-fotos",
            label: "Galería",
            hint: "Fotos reales de las habitaciones, el jardín y los talleres.",
          },
          {
            to: "/residencia-permanente",
            label: "Residencia permanente",
            hint: "Qué incluye la estancia y cómo es el ingreso.",
          },
          {
            to: "/contacto",
            label: "Contacto y ubicación",
            hint: "Periférico Sur 1, Col. Emiliano Zapata, Cuernavaca.",
          },
        ]}
      />
    </>
  );
}
